import { useCallback, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";

/**
 * useI18nPatch — QUESTIONNAIRE-2.0-A-S2-β-A (stand-alone hook).
 *
 * Wraps `PATCH /api/admin/questionnaires/{qid}/i18n` (S2-α backend, already
 * live). Encapsulates `if_version`, `expected_previous_status`, 200/409/422
 * routing, loading + error state, and an `edit_session_id` that stays
 * stable for the lifetime of the consumer component.
 *
 * Not yet consumed by `QuestionnaireBuilder.jsx` — that integration lands
 * in β-B. This hook can be unit-tested or wired into an isolated dev page.
 *
 * Contract recap (analysis §5):
 *   Request body:
 *     { if_version, edit_session_id?, updates: [{ path, value?, status?,
 *       expected_previous_status? }, ...] }
 *   Response 200:
 *     { questionnaire_id, version, applied_updates, needs_update_triggered[],
 *       edit_session_id, updated_at, admin_user_id, audit_event_id }
 *   409 codes: errors.version_conflict | errors.status_conflict
 *   422 codes: errors.invalid_path | invalid_language | invalid_status |
 *              reviewed_requires_value | value_too_long | path_target_missing |
 *              too_many_updates | scaffold_required | cannot_skip_status_stages
 */

const _makeSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const _parseErrorDetail = (e) => {
  const detail = e?.response?.data?.detail;
  if (!detail) return { code: null, raw: extractErrorMessage(e) };
  if (typeof detail === "string") return { code: detail, raw: detail };
  if (typeof detail === "object") {
    return {
      code: detail.error || null,
      currentVersion: detail.current_version,
      expectedVersion: detail.expected_version,
      currentStatus: detail.current_status,
      path: detail.path,
      raw: detail,
    };
  }
  return { code: null, raw: detail };
};

function useI18nPatch({
  qid,
  getCurrentVersion,           // () => number — read latest version from caller state
  onSuccess,                   // (responseBody) => void
  onConflict,                  // (parsedDetail) => void  (409)
  onValidationError,           // (parsedDetail) => void  (422)
  onUnauthorized,              // (parsedDetail) => void  (401/403)
  onNotFound,                  // (parsedDetail) => void  (404)
} = {}) {
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);
  const [sessionId] = useState(_makeSessionId);

  const patch = useCallback(
    async (updates) => {
      if (!qid) throw new Error("useI18nPatch: qid is required");
      if (!Array.isArray(updates) || updates.length === 0) {
        throw new Error("useI18nPatch: at least one update required");
      }

      const if_version = typeof getCurrentVersion === "function" ? getCurrentVersion() : null;
      if (!if_version) {
        throw new Error("useI18nPatch: getCurrentVersion() must return a positive integer");
      }

      setBusy(true);
      setLastError(null);
      try {
        const { data } = await api.patch(`/admin/questionnaires/${qid}/i18n`, {
          if_version,
          edit_session_id: sessionId,
          updates,
        });
        setLastResponse(data);
        onSuccess?.(data);
        return data;
      } catch (e) {
        const parsed = _parseErrorDetail(e);
        const status = e?.response?.status;
        const errObj = { status, ...parsed };
        setLastError(errObj);
        if (status === 401 || status === 403) onUnauthorized?.(errObj);
        else if (status === 404) onNotFound?.(errObj);
        else if (status === 409) onConflict?.(errObj);
        else if (status === 422) onValidationError?.(errObj);
        throw errObj;
      } finally {
        setBusy(false);
      }
    },
    [qid, getCurrentVersion, sessionId, onSuccess, onConflict, onValidationError, onUnauthorized, onNotFound],
  );

  /** Convenience: PATCH a single field's value and/or status. */
  const patchField = useCallback(
    ({ path, value, status, expected_previous_status }) =>
      patch([{ path, value, status, expected_previous_status }]),
    [patch],
  );

  const reset = useCallback(() => {
    setBusy(false);
    setLastError(null);
    setLastResponse(null);
  }, []);

  return {
    patch,
    patchField,
    busy,
    lastError,
    lastResponse,
    sessionId,
    reset,
  };
}

export default useI18nPatch;
